import { queryOptions } from '@tanstack/react-query';
import * as guidedExperiencesApi from '../endpoints/guided-experiences';
import type { EligibleExperiencesResult } from '../endpoints/guided-experiences';
import type { GuidedExperienceStateWire } from '../schemas/guided-experience';

export const GUIDED_EXPERIENCE_QUERY_KEYS = {
  all: ['guided-experiences'] as const,
  /**
   * Eligible-experiences key, scoped by user id.
   *
   * The result is persisted to disk (see `eligibleExperiencesQueryOptions`), so
   * an unscoped key would let one account's eligibility satisfy another's read
   * on the same device -- the leak class already closed for `currentUser`.
   */
  eligible: (userId: string) =>
    [...GUIDED_EXPERIENCE_QUERY_KEYS.all, 'eligible', userId] as const,
  state: (experienceId: string) =>
    [...GUIDED_EXPERIENCE_QUERY_KEYS.all, 'state', experienceId] as const,
} as const;

/**
 * Eligible guided experiences (onboarding, checklists, prompts) and tours for
 * one user.
 *
 * Persisted (`meta.persist`) because the app's boot gate depends on it: a
 * returning user must not pay a gateway round-trip on cold start, against the
 * sub-2s target. Persistence is what makes the user-scoped key mandatory.
 *
 * Disabled until a user id exists, so the query never fires -- and never writes
 * an unattributable cache entry -- before the session resolves.
 */
export const eligibleExperiencesQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: GUIDED_EXPERIENCE_QUERY_KEYS.eligible(userId),
    queryFn: async (): Promise<EligibleExperiencesResult> => {
      return guidedExperiencesApi.getEligibleExperiences();
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    meta: { persist: true },
  });

export const experienceStateQueryOptions = (experienceId: string) =>
  queryOptions({
    queryKey: GUIDED_EXPERIENCE_QUERY_KEYS.state(experienceId),
    queryFn: async (): Promise<GuidedExperienceStateWire> => {
      return guidedExperiencesApi.getExperienceState(experienceId);
    },
    enabled: !!experienceId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 15,
    meta: { persist: false },
  });
